import type { FC, ReactNode } from 'react'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { formatSignedNumber } from '@/lib/format'
import { ChartFrame } from '@/components/charts/chart-frame'
import { ChartTooltip, ChartTooltipRow, Swatch } from '@/components/charts/chart-tooltip'
import { niceScale, topPercent, useActiveIndex } from '@/components/charts/chart-utils'

export interface DivergingDatum {
    key: string
    label: string
    tooltipLabel: string
    positive: number
    negative: number
}

interface DivergingColumnChartProps {
    data: DivergingDatum[]
    positiveLabel: string
    negativeLabel: string
    ariaLabel: string
    formatValue: (value: number) => string
    positiveColor?: string
    negativeColor?: string
    height?: number
}

// Gains above the zero line and losses below it, each with its own rounded data end
export const DivergingColumnChart: FC<DivergingColumnChartProps> = ({
    data,
    positiveLabel,
    negativeLabel,
    ariaLabel,
    formatValue,
    positiveColor = 'var(--chart-2)',
    negativeColor = 'var(--chart-1)',
    height = 260
}): ReactNode => {
    const count = data.length
    const { active, setActive, onKeyDown, onFocus, onBlur } = useActiveIndex(count)

    const scale = useMemo(() => niceScale(
        -Math.max(...data.map((datum) => datum.negative), 0),
        Math.max(...data.map((datum) => datum.positive), 0),
        5,
        true
    ), [data])

    const zero = topPercent(0, scale)
    const span = scale.max - scale.min
    const center = (index: number): number => ((index + 0.5) / count) * 100
    const activeDatum = active === null ? null : data[active]

    return (
        <div className='w-full'>
            <ChartFrame
                height={height}
                scale={scale}
                axisFormat='number'
                emphasizeZero
                xAxis={(
                    <div className='flex h-full'>
                        {data.map((datum, index) => (
                            <span
                                key={datum.key}
                                className={cn(
                                    'min-w-0 flex-1 pt-2 text-center text-[11px] leading-none whitespace-nowrap text-muted-foreground',
                                    count > 6 && index % 2 === 1 && 'max-sm:invisible'
                                )}
                            >
                                {datum.label}
                            </span>
                        ))}
                    </div>
                )}
            >
                <div
                    role='group'
                    aria-roledescription='grafik kolom dua arah'
                    aria-label={`${ariaLabel}. Gunakan tombol panah untuk membaca setiap kolom.`}
                    tabIndex={0}
                    className='absolute inset-0 flex rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/50'
                    onKeyDown={onKeyDown}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onPointerLeave={(event) => {
                        if (event.pointerType === 'mouse') setActive(null)
                    }}
                >
                    {data.map((datum, index) => (
                        <div
                            key={datum.key}
                            className={cn(
                                'relative h-full min-w-0 flex-1 transition-opacity duration-150',
                                active !== null && active !== index && 'opacity-40'
                            )}
                            onPointerEnter={() => setActive(index)}
                            onPointerDown={() => setActive(index)}
                        >
                            <div
                                className='absolute left-1/2 w-[54%] max-w-10 -translate-x-1/2 rounded-t-[4px]'
                                style={{ bottom: `${100 - zero}%`, height: `${span === 0 ? 0 : (datum.positive / span) * 100}%`, background: positiveColor }}
                            />

                            <div
                                className='absolute left-1/2 w-[54%] max-w-10 -translate-x-1/2 rounded-b-[4px]'
                                style={{ top: `${zero}%`, height: `${span === 0 ? 0 : (datum.negative / span) * 100}%`, background: negativeColor }}
                            />
                        </div>
                    ))}
                </div>

                {activeDatum && active !== null && (
                    <ChartTooltip
                        title={activeDatum.tooltipLabel}
                        x={center(active)}
                        footer={(
                            <ChartTooltipRow label='Pertumbuhan bersih' value={formatSignedNumber(activeDatum.positive - activeDatum.negative)} />
                        )}
                    >
                        <ChartTooltipRow swatch={<Swatch color={positiveColor} />} label={positiveLabel} value={`+${formatValue(activeDatum.positive)}`} />
                        <ChartTooltipRow swatch={<Swatch color={negativeColor} />} label={negativeLabel} value={`−${formatValue(activeDatum.negative)}`} />
                    </ChartTooltip>
                )}
            </ChartFrame>

            <p className='sr-only' aria-live='polite'>
                {activeDatum ? `${activeDatum.tooltipLabel}: ${positiveLabel} ${formatValue(activeDatum.positive)}, ${negativeLabel} ${formatValue(activeDatum.negative)}` : ''}
            </p>
        </div>
    )
}
